import { useState, useEffect, useCallback } from "react";
import { 
  listAccessRequests, 
  createAccessRequest, 
  deleteAccessRequest,
  updateAccessRequest
} from "@/services/accessRequests";
import { IAccessRequest } from "@/interfaces/accessRequests";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";

export function useAccessRequests({ filterInitial = {} } = {}) {
  const [requests, setRequests] = useState<IAccessRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial });
  const [totalItems, setTotalItems] = useState(0);
  // Modais e Drawers
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<IAccessRequest | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const loadRequests = useCallback(
    debounce(async (p: number, l: number, f: any, s?: any) => {
      setLoading(true);
      try {
        const res = await listAccessRequests({
          page: p,
          limit: l,
          filter: f,
          sort: s || undefined,
          includes: ["site", "status", "file", "technicians", "activities"],
        });
        setRequests(res.accessRequests || []);
        setTotalItems(Number(res.totalItems || 0));
      } catch (err: any) {
        console.error(err);
        toast.error("Erro ao carregar solicitações de acesso.");
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    loadRequests(page, limit, filter, sort);
  }, [page, limit, filter, sort, loadRequests]);

  const handleCreate = async (payload: any) => {
    setLoading(true);
    try {
      await createAccessRequest({ accessRequest: payload });
      toast.success("Solicitação de acesso criada com sucesso!");
      setIsWizardOpen(false);
      loadRequests(page, limit, filter, sort);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Erro ao criar solicitação.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Deseja realmente remover esta solicitação?")) return;
    setLoading(true);
    try {
      await deleteAccessRequest({ id });
      toast.success("Solicitação removida com sucesso!");
      loadRequests(page, limit, filter, sort);
    } catch (err: any) {
      toast.error("Erro ao remover solicitação.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number, payload: any) => {
    setLoading(true);
    try {
      await updateAccessRequest({ id, accessRequest: payload });
      toast.success("Solicitação atualizada com sucesso!");
      loadRequests(page, limit, filter, sort);
    } catch (err: any) {
      toast.error("Erro ao atualizar solicitação.");
    } finally {
      setLoading(false);
    }
  };

  return {
    requests,
    loading,
    page,
    setPage,
    limit,
    setLimit,
    totalItems,
    filter,
    handleFilter,
    sort,
    handleSort,
    isWizardOpen,
    setIsWizardOpen,
    selectedRequest,
    setSelectedRequest,
    isPreviewOpen,
    setIsPreviewOpen,
    handleCreate,
    handleDelete,
    handleUpdate,
    refresh: () => loadRequests(page, limit, filter, sort),
  };
}
